import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Settings, Bell, Shield, Eye, LogOut, User, ChevronRight, HelpCircle, Info, MessageCircle, Hexagon, Crown, ArrowLeftRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const FAQ_ITEMS = [
  {
    q: 'What does Honey Pot do?',
    a: 'Honey Pot protects creator affiliate links from being hijacked by discount extensions like Honey, Rakuten, and Capital One Shopping. We ensure creators get properly credited.'
  },
  {
    q: 'How do I earn points?',
    a: 'You earn 1 point for each protected link, 5 points for supporting a new creator, and 10 points for daily activity. You level up every 50 points.'
  },
  {
    q: 'Does it affect my browsing speed?',
    a: 'No. Honey Pot runs efficiently in the background with minimal impact on page load times and memory usage.'
  },
  {
    q: 'Is my data safe?',
    a: 'Absolutely. We only monitor affiliate links. No personal data or browsing history is collected or sold.'
  },
  {
    q: 'Which affiliate networks are supported?',
    a: 'We support Amazon Associates, ShareASale, CJ Affiliate, Rakuten, Impact, and hundreds more networks.'
  }
];

export default function SettingsPage({ onNavigate }) {
  const { user, token, logout, API, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    notifications: true,
    auto_protect: true,
    show_badges: true
  });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(res.data);
    } catch (e) { console.error(e); }
  }, [API, token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggleSetting = async (key) => {
    const updated = { ...settings, [key]: !settings[key] };
    setSettings(updated);
    try {
      await axios.put(`${API}/settings`, updated, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { console.error(e); }
  };

  return (
    <div className="p-5 space-y-5">
      <h2 className="font-heading font-bold text-lg text-white">Settings</h2>

      {/* Profile Card */}
      <div className="glass-card rounded-xl p-4 flex items-center gap-3">
        <div className="hex-clip w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-black" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-white">{user?.name}</p>
            {user?.email_verified ? (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Verified</span>
            ) : (
              <a href="/verify-email" className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20" data-testid="settings-verify-link">Unverified</a>
            )}
          </div>
          <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
        </div>
        <div className="text-right">
          <p className="text-xs font-mono text-amber-500">Lvl {user?.level || 1}</p>
        </div>
      </div>

      {/* Creator Hub */}
      <div className="space-y-1">
        <p className="text-xs font-bold text-zinc-400 mb-3">Creator</p>
        <button
          data-testid="settings-creator-hub"
          onClick={() => onNavigate && onNavigate('creator')}
          className="w-full flex items-center gap-3 glass-card rounded-xl px-4 py-3.5 hover:border-amber-500/20 transition-all"
        >
          <Crown className="w-4 h-4 text-amber-500" />
          <div className="flex-1 text-left">
            <span className="text-sm text-white">{user?.is_creator ? 'Creator Dashboard' : 'Become a Creator'}</span>
            <p className="text-[10px] text-zinc-600">{user?.is_creator ? 'Manage codes & analytics' : 'Track your links & manage codes'}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-600" />
        </button>
        {user?.is_creator && (
          <button
            data-testid="settings-switch-to-fan"
            onClick={async () => {
              try {
                await axios.post(`${API}/creator/deactivate`, {}, { headers: { Authorization: `Bearer ${token}` } });
                await refreshUser();
              } catch (e) { console.error(e); }
            }}
            className="w-full flex items-center gap-3 glass-card rounded-xl px-4 py-3.5 hover:border-white/10 transition-all"
          >
            <ArrowLeftRight className="w-4 h-4 text-zinc-400" />
            <div className="flex-1 text-left">
              <span className="text-sm text-zinc-300">Switch to Fan</span>
              <p className="text-[10px] text-zinc-600">Your creator data is preserved</p>
            </div>
          </button>
        )}
      </div>

      {/* Preferences */}
      <div className="space-y-1">
        <p className="text-xs font-bold text-zinc-400 mb-3">Preferences</p>
        
        <div className="glass-card rounded-xl divide-y divide-white/5">
          <div className="flex items-center justify-between px-4 py-3.5">
            <div className="flex items-center gap-3">
              <Bell className="w-4 h-4 text-zinc-400" />
              <div>
                <p className="text-sm text-white">Notifications</p>
                <p className="text-[10px] text-zinc-600">Show when links are protected</p>
              </div>
            </div>
            <Switch
              data-testid="settings-notifications-toggle"
              checked={settings.notifications}
              onCheckedChange={() => toggleSetting('notifications')}
              className="data-[state=checked]:bg-amber-500"
            />
          </div>

          <div className="flex items-center justify-between px-4 py-3.5">
            <div className="flex items-center gap-3">
              <Shield className="w-4 h-4 text-zinc-400" />
              <div>
                <p className="text-sm text-white">Auto-Protect</p>
                <p className="text-[10px] text-zinc-600">Automatically shield affiliate links</p>
              </div>
            </div>
            <Switch
              data-testid="settings-autoprotect-toggle"
              checked={settings.auto_protect}
              onCheckedChange={() => toggleSetting('auto_protect')}
              className="data-[state=checked]:bg-amber-500"
            />
          </div>

          <div className="flex items-center justify-between px-4 py-3.5">
            <div className="flex items-center gap-3">
              <Eye className="w-4 h-4 text-zinc-400" />
              <div>
                <p className="text-sm text-white">Show Badges</p>
                <p className="text-[10px] text-zinc-600">Display protection badges on pages</p>
              </div>
            </div>
            <Switch
              data-testid="settings-badges-toggle"
              checked={settings.show_badges}
              onCheckedChange={() => toggleSetting('show_badges')}
              className="data-[state=checked]:bg-amber-500"
            />
          </div>
        </div>
      </div>

      {/* Help & About */}
      <div className="space-y-1">
        <p className="text-xs font-bold text-zinc-400 mb-3">Support</p>

        {/* Help Dialog */}
        <Dialog>
          <DialogTrigger asChild>
            <button
              data-testid="settings-help-btn"
              className="w-full flex items-center gap-3 glass-card rounded-xl px-4 py-3.5 hover:border-amber-500/20 transition-all"
            >
              <HelpCircle className="w-4 h-4 text-zinc-400" />
              <span className="text-sm text-white flex-1 text-left">FAQ & Help</span>
              <ChevronRight className="w-4 h-4 text-zinc-600" />
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-lg">Frequently Asked Questions</DialogTitle>
            </DialogHeader>
            <Accordion type="single" collapsible className="w-full">
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-white/5">
                  <AccordionTrigger
                    data-testid={`faq-item-${i}`}
                    className="text-sm text-zinc-300 hover:text-white hover:no-underline py-3"
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-xs text-zinc-500 leading-relaxed pb-3">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </DialogContent>
        </Dialog>

        {/* About Dialog */}
        <Dialog>
          <DialogTrigger asChild>
            <button
              data-testid="settings-about-btn"
              className="w-full flex items-center gap-3 glass-card rounded-xl px-4 py-3.5 hover:border-amber-500/20 transition-all"
            >
              <Info className="w-4 h-4 text-zinc-400" />
              <span className="text-sm text-white flex-1 text-left">About Honey Pot</span>
              <ChevronRight className="w-4 h-4 text-zinc-600" />
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-lg flex items-center gap-2">
                <Hexagon className="w-5 h-5 text-amber-500 fill-amber-500/20" />
                About Honey Pot
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-zinc-400 leading-relaxed">
                Honey Pot protects the creator economy by preserving affiliate links from being hijacked by other browser extensions.
              </p>
              <div className="glass-card rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Version</span>
                  <span className="text-zinc-300 font-mono">1.3.0</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Build</span>
                  <span className="text-zinc-300 font-mono">2026.02</span>
                </div>
              </div>
              <p className="text-[10px] text-zinc-600 text-center">
                Made with care for content creators everywhere.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Logout */}
      <Separator className="bg-white/5" />
      
      <button
        data-testid="settings-logout-btn"
        onClick={logout}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/5 transition-colors"
      >
        <LogOut className="w-4 h-4" />
        <span className="text-sm font-medium">Sign Out</span>
      </button>
    </div>
  );
}
